import {
  Typography,
  Box,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  Tabs,
  Tab,
  CircularProgress
} from '@mui/material'
import React, { useState, useEffect } from 'react'
import { WalletModel } from 'src/common/api'
import { getOrganizationProfile } from 'src/common/api/organizations'
import { useAuth } from 'src/common/context/auth'
import { ChainName, WALLET_ICON, CHAIN_NATIVE_TOKEN } from 'src/common/static'
import SearchIcon from '@mui/icons-material/Search'
import AddIcon from '@mui/icons-material/Add'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import SupportIcon from '@mui/icons-material/Support'
import WalletCard from 'src/pages/Dashboard/WalletCard'
import { Modal, alertNotification } from 'src/ui'
import { trimId } from 'src/common/helpers'
import * as S from './styles'
import { logEvent } from '../../common/analytics/analytics'

type WalletType = 'MINTING' | 'TREASURY'

interface ExtendedWalletModel extends WalletModel {
  walletType?: string
}

const Wallets: React.FC = () => {
  const { user } = useAuth()
  const [wallets, setWallets] = useState<ExtendedWalletModel[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<WalletType>('MINTING')
  const [treasurySearchQuery, setTreasurySearchQuery] = useState('')
  const [mintingSearchQuery, setMintingSearchQuery] = useState('')
  const [filterChain, setFilterChain] = useState<ChainName | 'all'>('all')
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [addWalletModalOpen, setAddWalletModalOpen] = useState(false)
  const [walletName, setWalletName] = useState('')
  const [walletAddress, setWalletAddress] = useState('')
  const [selectedChain, setSelectedChain] = useState<ChainName>(ChainName.ETHEREUM)
  const [chainMenuAnchor, setChainMenuAnchor] = useState<null | HTMLElement>(null)
  const [walletNameError, setWalletNameError] = useState('')
  const [walletAddressError, setWalletAddressError] = useState('')
  const [apiError, setApiError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [walletToDelete, setWalletToDelete] = useState<ExtendedWalletModel | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  // Fetch organization blockchain details (wallets)
  useEffect(() => {
    const fetchOrganizationBlockchainDetails = async () => {
      if (!user?.organization_id) {
        setWallets([])
        setLoading(false)
        return
      }

      try {
        const [profileResponse, balancesResponse] = await Promise.all([
          getOrganizationProfile(),
          import('src/common/api/organizations').then((mod) => mod.getOrganizationBalances())
        ])

        const blockchainDetails = profileResponse.data.blockchainDetails || []
        const balances = balancesResponse.data

        // Transform BlockchainDetailsModel to WalletModel format for display
        const transformedWallets: ExtendedWalletModel[] = blockchainDetails.map((detail) => {
          const balanceKey = `${detail.address}:${detail.chain}`
          return {
            id: detail.id,
            address: detail.address,
            name: detail.name,
            chain: detail.chain,
            walletType: detail.walletType,
            balance: balances[balanceKey]
          }
        })
        setWallets(transformedWallets)
      } catch (err) {
        console.error('Failed to fetch organization blockchain details:', err)
        setWallets([])
      } finally {
        setLoading(false)
      }
    }

    fetchOrganizationBlockchainDetails()
  }, [user?.organization_id])

  useEffect(() => {
    const start = performance.now()
    return () => {
      const duration = (performance.now() - start) / 1000
      logEvent('time_spent', { page: 'WalletsPage', duration_s: duration })
    }
  }, [])

  // Get current search query based on active tab
  const currentSearchQuery = activeTab === 'TREASURY' ? treasurySearchQuery : mintingSearchQuery

  // Filter wallets by wallet type, search query, and chain
  const filteredWallets = wallets.filter((wallet) => {
    const matchesWalletType = wallet.walletType === activeTab
    const matchesSearch =
      wallet.name.toLowerCase().includes(currentSearchQuery.toLowerCase()) ||
      wallet.address.toLowerCase().includes(currentSearchQuery.toLowerCase())
    const matchesChain = filterChain === 'all' || wallet.chain === filterChain
    return matchesWalletType && matchesSearch && matchesChain
  })

  const handleTabChange = (_event: React.SyntheticEvent, newValue: WalletType) => {
    setActiveTab(newValue)
    // Reset chain filter when switching tabs
    setFilterChain('all')
  }

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleFilterClose = () => {
    setAnchorEl(null)
  }

  const handleFilterSelect = (chain: ChainName | 'all') => {
    setFilterChain(chain)
    handleFilterClose()
  }

  const getFilterLabel = () => {
    if (filterChain === 'all') return 'All'
    return chainOptions.find((option) => option.value === filterChain)?.label || ''
  }

  const chainOptions: Array<{ value: ChainName | 'all'; label: string; icon?: React.ComponentType }> = [
    { value: 'all', label: 'All' },
    { value: ChainName.ETHEREUM, label: 'Ethereum', icon: WALLET_ICON[ChainName.ETHEREUM] },
    { value: ChainName.BASE, label: 'Base', icon: WALLET_ICON[ChainName.BASE] }
  ]

  const addWalletChainOptions: Array<{ value: ChainName; label: string; icon: React.ComponentType; token: string }> = [
    {
      value: ChainName.ETHEREUM,
      label: 'Ethereum',
      icon: WALLET_ICON[ChainName.ETHEREUM],
      token: CHAIN_NATIVE_TOKEN[ChainName.ETHEREUM]
    },
    {
      value: ChainName.BASE,
      label: 'Base',
      icon: WALLET_ICON[ChainName.BASE],
      token: CHAIN_NATIVE_TOKEN[ChainName.BASE]
    }
  ]

  const handleOpenAddWalletModal = () => {
    setAddWalletModalOpen(true)
    logEvent('button_click', { buttonName: 'Add Treasury Wallet Button' })
  }

  const handleCloseAddWalletModal = () => {
    setAddWalletModalOpen(false)
    setWalletName('')
    setWalletAddress('')
    setSelectedChain(ChainName.ETHEREUM)
    setWalletNameError('')
    setWalletAddressError('')
    setApiError('')
  }

  const handleChainMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setChainMenuAnchor(event.currentTarget)
  }

  const handleChainMenuClose = () => {
    setChainMenuAnchor(null)
  }

  const handleChainSelect = (chain: ChainName) => {
    setSelectedChain(chain)
    handleChainMenuClose()
  }

  const handleAddWallet = async () => {
    // Clear previous errors
    setWalletNameError('')
    setWalletAddressError('')
    setApiError('')

    // Validate inputs
    let hasError = false
    if (!walletName.trim()) {
      setWalletNameError('Wallet name is required')
      hasError = true
    }
    if (!walletAddress.trim()) {
      setWalletAddressError('Wallet address is required')
      hasError = true
    }

    if (hasError) return

    setIsSubmitting(true)

    try {
      // Import createBlockchain Details API
      const { createBlockchainDetails } = await import('src/common/api/organizations')

      // Call API to create treasury wallet
      await createBlockchainDetails({
        blockchainDetails: [
          {
            name: walletName,
            address: walletAddress,
            chain: selectedChain,
            walletType: 'TREASURY'
          }
        ]
      })

      // Refresh wallet list
      const [profileResponse, balancesResponse] = await Promise.all([
        getOrganizationProfile(),
        import('src/common/api/organizations').then((mod) => mod.getOrganizationBalances())
      ])

      const blockchainDetails = profileResponse.data.blockchainDetails || []
      const balances = balancesResponse.data

      const transformedWallets: ExtendedWalletModel[] = blockchainDetails.map((detail) => {
        // Use composite key: address:chain to match backend response
        const balanceKey = `${detail.address}:${detail.chain}`
        return {
          id: detail.id,
          address: detail.address,
          name: detail.name,
          chain: detail.chain,
          walletType: detail.walletType,
          balance: balances[balanceKey]
        }
      })
      setWallets(transformedWallets)

      // Close modal and reset form
      handleCloseAddWalletModal()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Failed to add wallet:', error)
      setApiError('Unable to create treasury wallet')
    } finally {
      setIsSubmitting(false)
    }
  }

  const selectedChainOption = addWalletChainOptions.find((opt) => opt.value === selectedChain)

  const handleContactSupport = () => {
    logEvent('button_click', { buttonName: 'Contact Support Button' })
    window.location.href = 'mailto:support@sdc.stablecorp.ca'
  }

  const handleDeleteClick = (walletId: string) => {
    logEvent('button_click', { buttonName: 'Delete Treasury Wallet Button' })
    const wallet = wallets.find((w) => w.id === walletId)
    if (wallet) {
      setWalletToDelete(wallet)
      setDeleteModalOpen(true)
    }
  }

  const handleDeleteConfirm = async () => {
    logEvent('button_click', { buttonName: 'Confirm Delete Treasury Wallet Button' })
    if (!walletToDelete?.id) return

    setIsDeleting(true)
    try {
      const { deleteBlockchainDetails } = await import('src/common/api/organizations')
      await deleteBlockchainDetails(walletToDelete.id)

      // Refresh wallet list
      const [profileResponse, balancesResponse] = await Promise.all([
        getOrganizationProfile(),
        import('src/common/api/organizations').then((mod) => mod.getOrganizationBalances())
      ])

      const blockchainDetails = profileResponse.data.blockchainDetails || []
      const balances = balancesResponse.data

      const transformedWallets: ExtendedWalletModel[] = blockchainDetails.map((detail) => {
        const balanceKey = `${detail.address}:${detail.chain}`
        return {
          id: detail.id,
          address: detail.address,
          name: detail.name,
          chain: detail.chain,
          walletType: detail.walletType,
          balance: balances[balanceKey]
        }
      })
      setWallets(transformedWallets)

      alertNotification('Treasury wallet deleted successfully', 'success')
      setDeleteModalOpen(false)
      setWalletToDelete(null)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      console.error('Failed to delete wallet:', error)
      const errorMessage =
        error?.response?.data?.message || 'Failed to delete treasury wallet. Please try again or contact support.'
      alertNotification(errorMessage, 'error')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteClose = () => {
    setDeleteModalOpen(false)
    setWalletToDelete(null)
  }

  return (
    <S.StyledContainer>
      <S.StyledPaper variant="card">
        <S.Flex mb="24px">
          <Box sx={{ borderBottom: 1, borderColor: 'divider', flex: 1 }}>
            <Tabs value={activeTab} onChange={handleTabChange} aria-label="wallet type tabs">
              <Tab label="Minting Wallets" value="MINTING" />
              <Tab label="Treasury Wallets" value="TREASURY" />
            </Tabs>
          </Box>
          <Box display="flex" alignItems="center" gap={2}>
            <TextField
              placeholder="Search"
              value={currentSearchQuery}
              onChange={(e) => {
                if (activeTab === 'TREASURY') {
                  setTreasurySearchQuery(e.target.value)
                } else {
                  setMintingSearchQuery(e.target.value)
                }
              }}
              size="small"
              sx={{
                width: '200px',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '8px',
                  backgroundColor: 'grey.100',
                  '& fieldset': {
                    borderColor: 'transparent'
                  },
                  '&:hover fieldset': {
                    borderColor: 'transparent'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'transparent'
                  }
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: 'text.secondary', fontSize: '20px' }} />
                  </InputAdornment>
                )
              }}
            />
            <Button
              variant="contained"
              color="inherit"
              onClick={handleFilterClick}
              endIcon={<ExpandMoreIcon />}
              sx={{
                borderRadius: '8px',
                backgroundColor: 'grey.100',
                color: 'text.primary',
                textTransform: 'none',
                fontWeight: 400,
                minWidth: '100px',
                '&:hover': {
                  backgroundColor: 'grey.200'
                }
              }}
            >
              {getFilterLabel()}
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleFilterClose}
              PaperProps={{
                sx: {
                  borderRadius: '8px',
                  mt: 1,
                  minWidth: '150px'
                }
              }}
            >
              {chainOptions.map((option) => (
                <MenuItem
                  key={option.value}
                  onClick={() => handleFilterSelect(option.value)}
                  selected={filterChain === option.value}
                >
                  {option.icon && (
                    <ListItemIcon sx={{ minWidth: '40px' }}>{React.createElement(option.icon)}</ListItemIcon>
                  )}
                  <Typography>{option.label}</Typography>
                </MenuItem>
              ))}
            </Menu>
            {activeTab === 'TREASURY' ? (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAddWalletModal}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Add Wallet
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                startIcon={<SupportIcon />}
                onClick={handleContactSupport}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500
                }}
              >
                Contact Support
              </Button>
            )}
          </Box>
        </S.Flex>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography>Loading wallets...</Typography>
          </Box>
        ) : filteredWallets.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography color="text.secondary">No wallets found</Typography>
          </Box>
        ) : (
          <Grid container spacing={3}>
            {filteredWallets.map((wallet) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={wallet.id}>
                <Box sx={{ '& > *': { mt: 0 } }}>
                  <WalletCard wallet={wallet} onDelete={handleDeleteClick} />
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
        <Modal open={addWalletModalOpen} onClose={handleCloseAddWalletModal} title="Add Treasury Wallet" maxWidth="sm">
          <Box>
            <Box mb={3}>
              <Typography variant="body2" fontWeight={600} mb={1.5}>
                Wallet Name
              </Typography>
              <TextField
                placeholder="Wallet Name"
                value={walletName}
                onChange={(e) => {
                  setWalletName(e.target.value)
                  setWalletNameError('')
                }}
                fullWidth
                size="small"
                error={!!walletNameError}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '8px',
                    backgroundColor: 'grey.100',
                    '& fieldset': {
                      borderColor: walletNameError ? 'error.main' : 'transparent'
                    },
                    '&:hover fieldset': {
                      borderColor: walletNameError ? 'error.main' : 'transparent'
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: walletNameError ? 'error.main' : 'transparent'
                    }
                  }
                }}
              />
              {walletNameError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {walletNameError}
                </Typography>
              )}
            </Box>
            <Box mb={3}>
              <Typography variant="body2" fontWeight={600} mb={1.5}>
                Wallet Address
              </Typography>
              <Box display="flex" gap={1}>
                <TextField
                  placeholder="Wallet Address"
                  value={walletAddress}
                  onChange={(e) => {
                    setWalletAddress(e.target.value)
                    setWalletAddressError('')
                  }}
                  fullWidth
                  size="small"
                  error={!!walletAddressError}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      backgroundColor: 'grey.100',
                      '& fieldset': {
                        borderColor: walletAddressError ? 'error.main' : 'transparent'
                      },
                      '&:hover fieldset': {
                        borderColor: walletAddressError ? 'error.main' : 'transparent'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: walletAddressError ? 'error.main' : 'transparent'
                      }
                    }
                  }}
                />
                <Button
                  variant="contained"
                  color="inherit"
                  onClick={handleChainMenuClick}
                  endIcon={<ExpandMoreIcon />}
                  sx={{
                    borderRadius: '8px',
                    backgroundColor: 'grey.100',
                    color: 'text.primary',
                    textTransform: 'none',
                    fontWeight: 400,
                    minWidth: '120px',
                    '&:hover': {
                      backgroundColor: 'grey.200'
                    }
                  }}
                >
                  {selectedChainOption && (
                    <Box display="flex" alignItems="center" gap={0.5}>
                      {React.createElement(selectedChainOption.icon)}
                      <Typography>{selectedChainOption.token}</Typography>
                    </Box>
                  )}
                </Button>
                <Menu
                  anchorEl={chainMenuAnchor}
                  open={Boolean(chainMenuAnchor)}
                  onClose={handleChainMenuClose}
                  PaperProps={{
                    sx: {
                      borderRadius: '8px',
                      mt: 1,
                      minWidth: '150px'
                    }
                  }}
                >
                  {addWalletChainOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      onClick={() => handleChainSelect(option.value)}
                      selected={selectedChain === option.value}
                    >
                      <ListItemIcon sx={{ minWidth: '40px' }}>{React.createElement(option.icon)}</ListItemIcon>
                      <Typography>{option.label}</Typography>
                    </MenuItem>
                  ))}
                </Menu>
              </Box>
              {walletAddressError && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                  {walletAddressError}
                </Typography>
              )}
            </Box>
            {apiError && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {apiError}
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleAddWallet}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                mb: 2,
                py: 1.5
              }}
            >
              {isSubmitting ? 'Adding Treasury Wallet...' : 'Add Treasury Wallet'}
            </Button>
            {/* TODO: Add terms of use and privacy policy */}
            {/* <Typography variant="body2" color="text.secondary" textAlign="center">
            By adding a wallet you confirm that you have read and agree to the{' '}
            <Link
              href="https://www.sedarplus.ca/csa-party/records/document.html?id=194b25dd3a2d2a8d59e108b3cf223b2fc41d68e33507899cbb350a659a9fea0a"
              target="_blank"
              rel="noopener"
              underline="always"
              color="primary"
            >
              Terms of Use
            </Link>{' '}
            &{' '}
            <Link
              href="https://stablecorp.ca/legal/privacy-policy"
              target="_blank"
              rel="noopener"
              underline="always"
              color="primary"
            >
              Privacy Policy
            </Link>
            .
          </Typography> */}
          </Box>
        </Modal>
        <Modal open={deleteModalOpen} onClose={handleDeleteClose} title="Confirm Wallet Deletion" maxWidth="xs">
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please confirm that you want to delete the following treasury wallet
            </Typography>

            {walletToDelete && (
              <Box sx={{ mb: 3 }}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Wallet Name
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {walletToDelete.name}
                  </Typography>
                </Box>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Wallet Address
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {trimId(walletToDelete.address)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    Chain
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {walletToDelete.chain}
                  </Typography>
                </Box>
              </Box>
            )}

            <Button
              fullWidth
              variant="contained"
              color="error"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={20} color="inherit" /> : null}
              sx={{
                borderRadius: '8px',
                textTransform: 'none',
                fontWeight: 500,
                mb: 2,
                py: 1.5
              }}
            >
              {isDeleting ? 'Deleting...' : 'Confirm Delete'}
            </Button>
          </Box>
        </Modal>
      </S.StyledPaper>
    </S.StyledContainer>
  )
}

export default Wallets
